import { authClient } from '@aimfora/auth/client'
import { m } from '@aimfora/i18n'
import { focusFirstInvalid } from '@aimfora/ui'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { z } from 'zod'
import { AuthCard, textLinkClass } from '~/components/auth-card'
import { FormError, SubmitButton, TextField } from '~/components/form-parts'
import { InlineText } from '~/components/legal-page'
import { getPublicConfig, getSessionUser } from '~/functions/session.functions'
import { authErrorMessage } from '~/lib/auth-errors'
import { safeRedirect } from '~/lib/redirect'
import { signupSchema } from '~/lib/schemas'

export const Route = createFileRoute('/signup')({
  validateSearch: z.object({
    redirect: z.string().optional(),
    email: z.string().optional(),
  }),
  beforeLoad: async ({ search }) => {
    if (await getSessionUser()) throw redirect({ href: safeRedirect(search.redirect) })
  },
  loader: () => getPublicConfig(),
  head: () => ({ meta: [{ title: `${m.auth_signup_title()} – ${m.app_name()}` }] }),
  component: SignupPage,
})

function SignupPage() {
  const search = Route.useSearch()
  const config = Route.useLoaderData()
  const router = useRouter()
  const formRef = useRef<HTMLFormElement>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { name: '', email: search.email ?? '', password: '' },
    validators: { onBlur: signupSchema(), onSubmit: signupSchema() },
    onSubmitInvalid: () => requestAnimationFrame(() => focusFirstInvalid(formRef.current)),
    onSubmit: async ({ value }) => {
      setServerError(null)
      const email = value.email.trim()
      const target = safeRedirect(search.redirect)
      const { error } = await authClient.signUp.email({
        name: value.name.trim(),
        email,
        password: value.password,
        // After the verification link the visitor is signed in and passed on to where they were going
        callbackURL: `/verify-email?redirect=${encodeURIComponent(target)}`,
      })
      if (error) {
        setServerError(authErrorMessage(error))
        return
      }
      await router.navigate({ to: '/verify-email', search: { email, redirect: search.redirect } })
    },
  })

  return (
    <AuthCard
      title={m.auth_signup_title()}
      footer={
        <>
          {m.auth_have_account()}{' '}
          <Link className={textLinkClass} to="/login" search={{ redirect: search.redirect }}>
            {m.auth_login_link()}
          </Link>
        </>
      }
    >
      <form
        ref={formRef}
        noValidate
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit()
        }}
      >
        <p className="-mt-1 text-ink-soft text-sm">
          {config.signupMode === 'open'
            ? m.auth_signup_intro_open()
            : m.auth_signup_intro_invite_only()}
        </p>
        <FormError message={serverError} />
        <form.Field name="name">
          {(field) => (
            <TextField field={field} label={m.auth_name_label()} autoComplete="name" autoFocus />
          )}
        </form.Field>
        <form.Field name="email">
          {(field) => (
            <TextField
              field={field}
              label={m.auth_email_label()}
              type="email"
              autoComplete="email"
            />
          )}
        </form.Field>
        <form.Field name="password">
          {(field) => (
            <TextField
              field={field}
              label={m.auth_password_label()}
              hint={m.auth_password_hint()}
              type="password"
              autoComplete="new-password"
              minLength={8}
            />
          )}
        </form.Field>
        <p className="text-ink-soft text-sm">
          <InlineText text={m.legal_signup_agree()} />
        </p>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(pending) => (
            <SubmitButton pending={pending} className="w-full">
              {m.auth_signup_submit()}
            </SubmitButton>
          )}
        </form.Subscribe>
      </form>
    </AuthCard>
  )
}
